import { ArrowsInSimple, ArrowSquareOut, Image } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  ScrollArea,
  Spinner,
  Text,
} from "@radix-ui/themes";

import { CommentsList } from "./CommentsList";

import { useTicketViewData } from "../hooks/useTicketView";

interface TicketViewPaneProps {
  ticketId: string;
  onDismiss: () => void;
  reviewStatus?: string | null;
}

const STATUS_COLORS: Record<
  string,
  "green" | "orange" | "blue" | "gray" | "red"
> = {
  approved: "green",
  complete: "green",
  needs_review: "orange",
  wip: "blue",
  incomplete: "red",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  complete: "Complete",
  needs_review: "Needs Review",
  wip: "In Progress",
  incomplete: "Incomplete",
};

export function TicketViewPane({
  ticketId,
  onDismiss,
  reviewStatus,
}: TicketViewPaneProps) {
  const { ticket, comments, isLoading } = useTicketViewData(ticketId);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Flex direction="column" align="center" gap="2">
          <Spinner size="3" />
          <Text size="2" className="text-puntt-neutral-gray-9">
            Loading ticket...
          </Text>
        </Flex>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          Ticket not found
        </Text>
      </div>
    );
  }

  const status = ticket.status ?? "wip";
  const latestBoard = ticket.revisionBoards?.filter((b) => !b.deletedAt).at(-1);
  const thumbnails = ticket.thumbnails ?? [];
  const reviewFiles = latestBoard?.reviewFiles ?? [];

  return (
    <div
      className="flex h-full flex-col bg-base-white"
      data-testid="ticket-view-pane"
    >
      {/* Header */}
      <Flex
        align="center"
        justify="between"
        className="shrink-0 border-b border-puntt-neutral-gray-5 px-4 py-3"
      >
        <Flex align="center" gap="2" className="min-w-0 flex-1">
          <Text
            size="2"
            weight="bold"
            className="truncate text-puntt-neutral-gray-12"
          >
            {ticket.title}
          </Text>
          <Badge
            size="1"
            variant="soft"
            color={STATUS_COLORS[status] ?? "gray"}
          >
            {STATUS_LABELS[status] ?? status}
          </Badge>
          {reviewStatus && (
            <Text size="1" className="animate-pulse italic text-puntt-accent-9">
              {reviewStatus}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap="1" className="shrink-0">
          <Button size="1" variant="ghost" asChild className="cursor-pointer">
            <a
              href={`/tickets/${ticketId}/view?version=0`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowSquareOut size={14} />
            </a>
          </Button>
          <Button
            size="1"
            variant="ghost"
            onClick={onDismiss}
            className="cursor-pointer"
          >
            <ArrowsInSimple size={14} />
          </Button>
        </Flex>
      </Flex>

      {/* Body */}
      <ScrollArea className="flex-1">
        <div className="space-y-4 p-4">
          {/* Images section */}
          {(thumbnails.length > 0 || reviewFiles.length > 0) && (
            <div>
              <Flex align="center" gap="1" className="mb-2">
                <Image size={14} className="text-puntt-neutral-gray-9" />
                <Text
                  size="1"
                  weight="medium"
                  className="text-puntt-neutral-gray-9"
                >
                  Images
                </Text>
              </Flex>
              <div className="grid grid-cols-2 gap-2">
                {thumbnails.map((thumb, i) => (
                  <div
                    key={`thumb-${i}`}
                    className="overflow-hidden rounded-md border border-puntt-neutral-gray-5"
                  >
                    <img
                      src={thumb}
                      alt={`Page ${i + 1}`}
                      className="h-auto w-full object-contain"
                    />
                  </div>
                ))}
                {thumbnails.length === 0 &&
                  reviewFiles.map((rf, i) => (
                    <div
                      key={`rf-${i}`}
                      className="overflow-hidden rounded-md border border-puntt-neutral-gray-5"
                    >
                      <img
                        src={rf.source}
                        alt={`Review file ${i + 1}`}
                        className="h-auto w-full object-contain"
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Latest revision board */}
          {latestBoard && (
            <div>
              <Text
                size="1"
                weight="medium"
                className="mb-1 block text-puntt-neutral-gray-9"
              >
                Latest Revision:{" "}
                {latestBoard.name ?? `Revision ${latestBoard.revision ?? ""}`}
              </Text>
              <Flex gap="2" wrap="wrap">
                {latestBoard.isAIReviewed && (
                  <Badge size="1" variant="soft" color="blue">
                    AI Reviewed
                  </Badge>
                )}
                <Badge size="1" variant="soft" color="gray">
                  {latestBoard.reviews?.length ?? 0} reviews
                </Badge>
              </Flex>
            </div>
          )}

          {/* Comments section */}
          <CommentsList comments={comments ?? []} />
        </div>
      </ScrollArea>
    </div>
  );
}
